import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSize,
  GenericAlert,
  GenericButton,
  Icon,
  Tooltip,
} from "@studio3t/generic-components";
import { useState } from "react";
import styled from "styled-components";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { seedingWaitLabel, seedingWaits } from "./deployProblems";
import { useFixWithAi } from "./EditorErrorsPopover";
import {
  lintBlockReason,
  type StudioView,
  usePipelineStaging,
} from "./pipelineStaging";
import { StageProgressBar } from "./StageProgressBar";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { usePipelineCode } from "./useAiHelperProps";
import { RUN_SKIPS, useRunSkips } from "./useRunSkipsOperations";
import { PIPELINE_RUN_ID, useScriptRun } from "./useScriptRun";

const SEMANTIC = designVariables.ui.semantic;
const BTN = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

// Figma Bridge_Pipeline/toolbar (14896-92090): "Run pipeline" on the left,
// the stage progress in the middle, the two-segment view switch on the
// right, 10px padding, mainPrimary hairline underneath.
const VIEWS: ReadonlyArray<{ id: StudioView; label: string }> = [
  { id: "stages", label: "Stages" },
  { id: "full", label: "Full pipeline" },
];

export function StudioToolbar() {
  const {
    studioView: view,
    setStudioView: setView,
    pipelineCodeLintStatus,
    pipelineCodeErrors,
  } = usePipelineStaging();
  // The dialog's "Fix with AI": the editor's Errors popover recipe —
  // the helper opens if collapsed and gets the lint errors as the message,
  // with the code itself (its context item needs code that parses).
  const { code } = usePipelineCode();
  const fixWithAi = useFixWithAi("pipeline code", pipelineCodeErrors, code);
  const fullBlockReason = useFullViewWait();
  // Run pipeline chains every staged transform (last block's schema on the
  // final output) into the Output panel — /studio's Run All, staged-shaped.
  const { runPipeline, runningId } = useScriptRun();
  // The disabled button swallows pointer events, so the tooltip trigger
  // wraps it; hidden while nothing blocks.
  const blockReason = lintBlockReason(view, pipelineCodeLintStatus);
  // Said on the button whenever the pipeline has a window or chooses
  // operations: a run applies neither.
  const skips = useRunSkips();
  const runNote = skips ? RUN_SKIPS[skips] : null;
  const runTooltip = blockReason ?? runNote;
  // A running pipeline is view-only (Figma 14894-157225): the left slot
  // carries the explanation instead of Run.
  const { editsLocked, prepareStagesSwitch } = useStudioPipeline();
  // Full → Stages goes through the hand-over (StagesSwitch): the modal
  // shows when the code has errors. It doesn't repeat them — they're the
  // editor's lint diagnostics, right behind it — it only says to fix them.
  const [codeErrors, setCodeErrors] = useState(false);
  const selectView = (id: StudioView) => {
    if (id !== "stages" || view !== "full") {
      setView(id);
      return;
    }
    void prepareStagesSwitch().then((result) => {
      if (result.kind === "errors") setCodeErrors(true);
    });
  };

  return (
    <>
      <Bar data-testid="studio-toolbar" $viewOnly={editsLocked}>
        {editsLocked ? (
          <ToolbarAlert
            state="Info"
            icon
            dismissible={false}
            message="Running pipelines can only be viewed. Stop the pipeline first if you need to make any edits."
            testId="view-only-alert"
          />
        ) : (
          <Tooltip text={runTooltip ?? ""} side="bottom" hide={!runTooltip}>
            <RunButton
              text="Run pipeline"
              type="primary"
              size={ButtonSize.Small}
              icon={Icon.Run}
              iconPosition="leading"
              disabled={!!blockReason || runningId === PIPELINE_RUN_ID}
              onClick={runPipeline}
            />
          </Tooltip>
        )}
        {/* The view-only studio keeps the middle empty: nothing to fill in. */}
        <Center>{!editsLocked && <StageProgressBar />}</Center>
        <ViewSwitch
          view={view}
          fullBlockReason={fullBlockReason}
          onSelect={selectView}
        />
      </Bar>
      <ConfirmDeleteModal
        isOpen={codeErrors}
        title="Fix the code errors first"
        confirmType="primary"
        confirmLabel="OK"
        hideCancel
        message="The pipeline code has errors and can't be shown as stages until they're fixed."
        extraAction={{
          label: "Fix with AI",
          onClick: () => {
            setCodeErrors(false);
            fixWithAi();
          },
        }}
        onConfirm={() => setCodeErrors(false)}
        onCancel={() => setCodeErrors(false)}
      />
    </>
  );
}

// Stages → Full waits for a dropped stage's schema seed (the same wait
// Deploy shows): the Full view's code is generated from staging, and an
// edit made there would be thrown away when the landing seed regenerates
// it. A few seconds at most; the returned label is what the tooltip says.
function useFullViewWait(): string | null {
  const {
    studioView: view,
    seedingStageIds,
    sources,
    sinks,
  } = usePipelineStaging();
  if (view !== "stages") return null;
  const wait = seedingWaits(seedingStageIds, { sources, sinks })[0];
  return wait ? seedingWaitLabel(wait) : null;
}

// The two-segment view switch. The disabled segment swallows pointer
// events, so the tooltip trigger wraps the group; hidden while nothing
// waits.
function ViewSwitch({
  view,
  fullBlockReason,
  onSelect,
}: {
  view: StudioView;
  fullBlockReason: string | null;
  onSelect: (id: StudioView) => void;
}) {
  return (
    <Tooltip text={fullBlockReason ?? ""} side="bottom" hide={!fullBlockReason}>
      <SegmentedGroup>
        {VIEWS.map((item, index) => {
          const blocked = item.id === "full" && fullBlockReason !== null;
          return (
            <Segment
              key={item.id}
              type="button"
              $active={view === item.id}
              $first={index === 0}
              $last={index === VIEWS.length - 1}
              $z={VIEWS.length - index}
              disabled={blocked}
              aria-label={
                blocked ? `${item.label} — ${fullBlockReason}` : undefined
              }
              onClick={() => onSelect(item.id)}
            >
              {item.label}
            </Segment>
          );
        })}
      </SegmentedGroup>
    </Tooltip>
  );
}

// Three columns — left slot, progress, view switch — the outer two sharing
// the free width so the progress sits on the toolbar's centre line (Figma
// pins it there with fixed 240px side wraps; equal columns do the same
// without the fixed width).
// The view-only studio drops the mainPrimary accent (Figma 14894-157225):
// the hairline under the toolbar goes card1 gray with the rest of the
// workbench chrome. The alert (36px) is taller than the Run button (28px),
// so the bar's vertical padding shrinks to keep the 48px toolbar height.
const Bar = styled.div<{ $viewOnly?: boolean }>`
  flex-shrink: 0;
  display: grid;
  /* The progress bar hides by container query when the bar is too narrow
     for all three columns (Center). */
  container-type: inline-size;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  justify-items: start;
  padding: ${(p) => (p.$viewOnly ? ALERT_VERTICAL_INSET : SEMANTIC.spacings)}px
    ${SEMANTIC.spacings}px;
  border-bottom: 1px solid
    ${(p) => (p.$viewOnly ? p.theme.cards1Border : p.theme.semanticMainPrimary)};
`;

// The toolbar alert at the Figma Alert component's own metrics (14894-157225:
// Alert/padding 8, Alert/gap 6, 20px icon, Paragraph/Default/Regular 14px
// at 1.4 — 36px tall). The panels' StageAlert is sized up beyond that for
// its own slot, so this one carries its own sizing. Inline so the icon and
// the single line share the row, not the lib's block layout.
const ALERT_PADDING = 8;
const ALERT_GAP = 6;
const ALERT_VERTICAL_INSET = 6;
const ToolbarAlert = styled(GenericAlert)`
  display: inline-flex;
  align-items: center;
  width: auto;
  padding: ${ALERT_PADDING}px;
  gap: ${ALERT_GAP}px;
  font-size: 14px;
  line-height: 1.4;

  /* The lib reserves room after the text for a dismiss button (14px inner
     padding) and keeps empty action slots in the row, each taking a flex
     gap, even when nothing renders in them — the Figma alert hugs its text
     (Alert/gap only between icon and text), so the inner padding goes and
     the gap moves onto the icon, where the slot markup can't pad it out. */
  & > div {
    padding-right: 0;
  }
  & > div,
  & > div > div {
    gap: 0;
  }
  & img {
    margin-right: ${ALERT_GAP}px;
  }
`;

// Keyboard focus (TTTL-186): the primary button draws its border as an
// inset shadow on a white fill, and an inset ring would sit flush against
// it — the ring goes outside, past a gap.
const RunButton = styled(GenericButton)`
  --focus-ring-offset: 2px;
`;

// The progress readout is the toolbar's one dispensable column: below the
// width the three columns need side by side (TTTL-186, a narrow workbench)
// it hides rather than run under the view switch — the stage tabs below
// still carry the same counts.
const Center = styled.div`
  justify-self: center;

  @container (max-width: 580px) {
    display: none;
  }
`;

const SegmentedGroup = styled.div`
  display: inline-flex;
  align-items: center;
  justify-self: end;
`;

// Text flavour of PipelineDetail's SegmentedButton (styles.tsx), on the
// primary button palette per the Figma SegmentedButtonGroup (14265-192608):
// active is the light-green fill with the dark-green border, the rest are
// white with the mainPrimary border. 1px borders collapse via -1px overlap,
// corners only on the outer ends; the active (or hovered) segment is raised
// above its neighbours so its border wins the overlap.
const Segment = styled.button<{
  $active: boolean;
  $first: boolean;
  $last: boolean;
  $z: number;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${BTN.s.size}px;
  padding: 0 ${BTN.s.paddingw}px;
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme.buttonPrimaryActiveBorder
        : p.theme.buttonPrimaryDefaultBorder};
  background: ${(p) =>
    p.$active
      ? p.theme.buttonPrimaryActiveBackground
      : p.theme.buttonPrimaryDefaultBackground};
  color: ${(p) => p.theme.buttonPrimaryDefaultText};
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  margin-left: ${(p) => (p.$first ? "0" : "-1px")};
  z-index: ${(p) => (p.$active ? VIEWS.length + 1 : p.$z)};
  border-radius: ${(p) =>
    p.$first
      ? `${BTN.corner}px 0 0 ${BTN.corner}px`
      : p.$last
        ? `0 ${BTN.corner}px ${BTN.corner}px 0`
        : "0"};

  &:hover {
    background: ${(p) =>
      p.$active
        ? p.theme.buttonPrimaryActiveBackground
        : p.theme.buttonPrimaryHoverBackground};
    border-color: ${(p) =>
      p.$active
        ? p.theme.buttonPrimaryActiveBorder
        : p.theme.buttonPrimaryHoverBorder};
    z-index: ${VIEWS.length + 2};
  }

  /* Waiting on a schema seed (Stages → Full): the primary buttons'
     disabled face, no hover lift. */
  &:disabled {
    cursor: default;
    color: ${(p) => p.theme.semanticTextDisabled};
    background: ${(p) => p.theme.buttonPrimaryDefaultBackground};
    border-color: ${(p) => p.theme.buttonPrimaryDefaultBorder};
    z-index: ${(p) => p.$z};
  }

  /* Keyboard focus (TTTL-186): the segments carry the primary border and
     an active primary fill, which an inset ring would merge into — the
     ring sits outside, past a gap, and the focused segment rises above
     its neighbours so the ring is whole. */
  --focus-ring-offset: 2px;
  &:focus-visible {
    z-index: ${VIEWS.length + 2};
  }
`;
